import { useState, useMemo } from 'react';
import { TextField, FunctionField, WrapperField, Button, SimpleShowLayout, usePermissions, useTranslate } from 'react-admin';
import { Box, useTheme, useMediaQuery, Tooltip } from '@mui/material';
import { format } from 'date-fns';
import { formatIntervalSince } from '@/helpers/date/formatTimeInterval.js';
import CustomList from '@/components/global/list/CustomList.jsx';
import { CustomDatagridConfigurable } from '@/components/global/list/CustomDatagridConfigurable.jsx';
import ShowIcon from '@/assets/icons/show-icon.svg?react';
import UnassignedField from '@/components/global/fields/UnassignedField';
import CustomEditModalButton from '@/components/global/buttons/CustomEditModalButton.jsx';
import CustomShowModalButton from '@/components/global/buttons/CustomShowModalButton.jsx';
import CustomMorePopover from '@/components/global/buttons/CustomMorePopover.jsx';
import { useDrawer } from '@/composables/useDrawer.js';
import ListCollectionPointField from '@/components/collectionPoint/ListCollectionPointField.jsx';
import { getNewDeviceFilters } from '@/components/newDevice/list/newDeviceFilters.jsx';
import { StatusWithIcon } from '@/components/newDevice/fields/StatusWithIcon.jsx';
import { ConfigModal } from '@/components/newDevice/ConfigModal.jsx';
import { NewDeviceListActions } from '@/components/newDevice/buttons/NewDeviceListActions.jsx';
import { NewDeviceBulkForm } from '@/components/newDevice/forms/NewDeviceBulkForm.jsx';
import { ShowDeviceConfigButton } from '@/components/device/buttons/ShowDeviceConfigButton.jsx';

export { EditDrawer } from '@/components/newDevice/EditDrawer.jsx';

export const NewDeviceList = () => {
    const [configModalOpen, setConfigModalOpen] = useState(false);
    const [configValue, setConfigValue] = useState('');
    const { openDrawer } = useDrawer();
    const { isLoading, permissions } = usePermissions();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const translate = useTranslate();
    const isAdminOrBok = permissions?.isAdmin || permissions?.isBok;

    const filters = useMemo(() => getNewDeviceFilters(isMobile, translate), [isMobile, translate]);

    if (isLoading) return null;

    return (
        <>
            <ConfigModal open={configModalOpen} onClose={() => setConfigModalOpen(false)} value={configValue} />
            <CustomList
                filters={filters}
                filter={{ deploymentStatus_neq: 'deployed', withAggregates: true }}
                actions={<NewDeviceListActions />}
                addLastColumnShadow={true}
            >
                <CustomDatagridConfigurable bulkActionButtons={permissions.isAdmin ? <NewDeviceBulkForm /> : false} omit={['collectionType']}>
                    <FunctionField
                        label={translate('common.contractor')}
                        sortBy='contractorName'
                        render={record => {
                            const value = record?.contractorId;

                            return value ? (
                                <TextField
                                    source='contractorName'
                                    onClick={isAdminOrBok ? () => openDrawer(record.contractorId, 'contractor-edit') : undefined}
                                    sx={{ cursor: isAdminOrBok ? 'pointer' : 'default', '&:hover': { textDecoration: isAdminOrBok ? 'underline' : 'none' } }}
                                />
                            ) : (
                                <UnassignedField />
                            );
                        }}
                    />
                    <TextField source='serialNumber' label={translate('resources.device.fields.serialNumber')} />
                    <FunctionField
                        label={translate('common.status')}
                        sortBy='deploymentStatus'
                        render={record => <StatusWithIcon statusId={record.deploymentStatus} />}
                    />
                    <FunctionField
                        label={translate('common.collectionPoint')}
                        sortBy='collectionPoint.customId'
                        render={record => <ListCollectionPointField collectionPoint={record.collectionPoint} />}
                    />
                    <FunctionField
                        label={translate('resources.device.fields.productionDate')}
                        source='productionDate'
                        sx={{ '& .MuiTypography-root': { whiteSpace: 'nowrap' } }}
                        render={record => {
                            const value = record.productionDate;

                            return value ? (
                                <Tooltip
                                    title={formatIntervalSince(value) ? `${formatIntervalSince(value)} ${translate('notification.timeAgo')}` : ''}
                                    arrow
                                    classes={{ tooltip: 'tooltip-black' }}
                                >
                                    <span>{format(new Date(value), 'dd.MM.yyyy HH:mm')}</span>
                                </Tooltip>
                            ) : (
                                <UnassignedField />
                            );
                        }}
                    />
                    <FunctionField
                        label={translate('resources.device.fields.group')}
                        sortBy='group.name'
                        render={record => {
                            const value = record?.group?.id;

                            return value ? (
                                <Button onClick={() => openDrawer(value, 'device-group-show')} className='list-group' label={record.group.name} />
                            ) : (
                                <UnassignedField />
                            );
                        }}
                    />
                    <FunctionField
                        label={translate('resources.device.fields.collectionType')}
                        sortBy='collectionType'
                        source='collectionType'
                        render={record => {
                            const value = record?.collectionType;

                            return value ? translate(`resources.device.choices.collectionType.${value}`) : <UnassignedField />;
                        }}
                    />
                    <FunctionField
                        label={translate('resources.contractor.labels.config')}
                        render={record => {
                            const value = record?.config;
                            return value ? (
                                <Button
                                    onClick={() => {
                                        setConfigModalOpen(true);
                                        setConfigValue(record.config);
                                    }}
                                    startIcon={<ShowIcon />}
                                    label={translate('resources.device.fields.preview')}
                                />
                            ) : (
                                <UnassignedField />
                            );
                        }}
                    />
                    <WrapperField source='softwareSoc' label={translate('menu.software')} sortBy='software.softwareSoc'>
                        <Box display='flex'>
                            <SimpleShowLayout flex={1}>
                                <TextField source='softwareMcu' label={translate('resources.device.labels.mcu')} />
                            </SimpleShowLayout>
                            <SimpleShowLayout flex={1}>
                                <TextField source='softwareSoc' label={translate('resources.device.labels.soc')} />
                            </SimpleShowLayout>
                        </Box>
                    </WrapperField>
                    <FunctionField
                        label={translate('common.options')}
                        render={record => (
                            <CustomMorePopover permissions={permissions} record={record}>
                                <CustomShowModalButton
                                    onOpen={() => openDrawer(record.id, 'device-show')}
                                    label={translate('resources.device.labels.showDevice')}
                                />
                                {permissions.isAdmin && (
                                    <CustomEditModalButton
                                        onOpen={() => openDrawer(record.id, 'new-device-edit')}
                                        label={translate('resources.device.labels.editDevice')}
                                    />
                                )}
                                <ShowDeviceConfigButton id={record.id} />
                            </CustomMorePopover>
                        )}
                    />
                </CustomDatagridConfigurable>
            </CustomList>
        </>
    );
};
